import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import StatusLayout from "./StatusLayout";
import { useCase } from "../../context/CaseContext";
import { fetchConfirmDetailsFields, submitConfirmDetails } from "../../lib/everifyStatusApi";
import { clampIsoDateYear, hasFourDigitYear } from "../../lib/dateUtils";

const buildLabel = (value = "") => {
  const normalized = value.toLowerCase();

  // Handle known acronyms
  if (normalized === "ssn") {
    return "SSN";
  }

  return value
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => {
      // Force US to stay uppercase
      if (word.toLowerCase() === "us") {
        return "US";
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};


const SSN_PATTERN = /^(?!123-45-6789|111-11-1111)\d{3}-\d{2}-\d{4}$/;

const formatSsn = (value = "") => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 3));
  if (digits.length > 3) parts.push(digits.slice(3, 5));
  if (digits.length > 5) parts.push(digits.slice(5));
  return parts.join("-");
};

const isDateField = (name = "") => name.toLowerCase().includes("date");

const ConfirmDetailsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeCaseNumber } = useCase();
  const [formValues, setFormValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["confirm-details-fields", activeCaseNumber],
    queryFn: () => fetchConfirmDetailsFields(activeCaseNumber),
    enabled: Boolean(activeCaseNumber),
  });

  const fields = useMemo(
    () =>
      (data ?? []).map((field) =>
        typeof field === "string"
          ? {
              field_Name: field,
              label: field,
              required: true,
              default_Value: "",
            }
          : field
      ),
    [data]
  );

  useEffect(() => {
    if (fields.length) {
      const defaults = {};
      fields.forEach((field) => {
        const key = field.field_Name || field.fieldName || field.name;
        if (!key) return;
        const defaultValue = field.default_Value ?? field.defaultValue ?? "";
        defaults[key] = isDateField(key) ? clampIsoDateYear(defaultValue) : defaultValue;
      });
      setFormValues(defaults);
      setFieldErrors({});
    }
  }, [fields]);

const inferFieldMeta = (name = "") => {
  const normalized = name.toLowerCase();
  const meta = { type: "text", maxLength: undefined };
  if (isDateField(name)) {
    meta.type = "date";
    meta.maxLength = 10;
  } else if (normalized.includes("email")) {
    meta.type = "email";
  } else if (normalized.includes("phone")) {
    meta.type = "tel";
    meta.maxLength = 15;
  } else if (normalized.includes("ssn")) {
    meta.type = "text";
    meta.maxLength = 11;
  } else if (normalized.includes("passport")) {
    meta.type = "text";
    meta.maxLength = 9;
  }
  return meta;
};

  const validateFieldValue = (name = "", value = "") => {
    const normalized = name.toLowerCase();
    if (!value) {
      return "This field is required.";
    }
    if (normalized.includes("date")) {
      if (!hasFourDigitYear(value)) {
        return "Year must be four digits.";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "Enter a valid date.";
      }
    }
    if (normalized.includes("ssn")) {
      if (!SSN_PATTERN.test(value)) {
        return "Enter SSN as ###-##-#### (not 123-45-6789 or 111-11-1111).";
      }
    }
    if (normalized.includes("email")) {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!pattern.test(value)) {
        return "Enter a valid email.";
      }
    }
    if (normalized.includes("passport")) {
      if (value.length > 9) {
        return "Passport number must be 9 characters or less.";
      }
    }
    return "";
  };

  const handleInputChange = (name, value) => {
    const normalized = name.toLowerCase();
    let nextValue = value;
    if (normalized.includes("ssn")) {
      nextValue = formatSsn(value);
    } else if (normalized.includes("passport")) {
      nextValue = value.slice(0, 9).toUpperCase();
    } else if (isDateField(name)) {
      nextValue = clampIsoDateYear(value);
    }
    setFormValues((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!activeCaseNumber || isSubmitting) return;
    const errors = {};
    fields.forEach((field) => {
      const key = field.field_Name || field.fieldName || field.name;
      if (!key) return;
      const validationMessage = validateFieldValue(key, formValues[key] ?? "");
      if (validationMessage) {
        errors[key] = validationMessage;
      }
    });
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    const payload = Object.entries(formValues).map(([key, value]) => ({
      field_name: key,
      value,
    }));

    setIsSubmitting(true);
    try {
      await submitConfirmDetails(activeCaseNumber, payload);
      await queryClient.invalidateQueries({ queryKey: ["everifyEmployees"] });
      toast.success("Details submitted correctly.");
      navigate("/");
    } catch (error) {
      toast.error("Unable to submit details. Please retry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!activeCaseNumber) {
    return (
      <StatusLayout title="Confirm Details" description="We need a case number to continue.">
        <div className="status-section">
          <p>Please create or select an E-Verify case first.</p>
        </div>
      </StatusLayout>
    );
  }

  return (
    <StatusLayout
      title="Data Mismatch"
      description="Some information does not match government records. Please review and update the data"
    >
      <div className="status-section">
        <h3>Review and Update</h3>
        {isLoading && <p>Loading fields...</p>}
        {isError && (
          <div>
            <p>Unable to load fields.</p>
            <button className="status-muted-button" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}
        {!isLoading && !isError && (
          <form
            className="status-form"
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
          >
            {fields.map((field) => {
              const key = field.field_Name || field.fieldName || field.name;
              if (!key) return null;
               const meta = inferFieldMeta(key);
              return (
                <label key={key}>
                  {buildLabel(field.label || key)}
                  <input
                    type={meta.type}
                    maxLength={meta.maxLength}
                    value={formValues[key] ?? ""}
                    onChange={(event) => handleInputChange(key, event.target.value)}
                    required={field.required}
                    aria-invalid={Boolean(fieldErrors[key])}
                  />
                  {fieldErrors[key] && <span className="status-error">{fieldErrors[key]}</span>}
                </label>
              );
            })}
            <div className="status-actions">
              <button type="submit" className="status-primary" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Case"}
              </button>
              <button
                type="button"
                className="status-muted-button"
                onClick={() => navigate("/case/tentative-nc")}
              >
                Back
              </button>
            </div>
          </form>
        )}
      </div>
    </StatusLayout>
  );
};

export default ConfirmDetailsPage;
